import { type FC } from 'react';
import useMeasure, { type RectReadOnly } from 'react-use-measure';
import { type Variant, type Variants } from 'framer-motion';

import { generateDetailedArc } from '@/common/lib/generateArcPoints';
import { type CommoditiesType } from '@/common/types/common';

import { ClaimItemAnimation } from '../ClaimItemAnimation';

type AnimationType = 'line' | 'arc';

interface ClaimCashCardAnimationProps {
  icon: {
    img?: string;
    webp?: string;
  };
  reward: CommoditiesType | 'cash' | 'str' | 'tgem';
  finalPositon?: RectReadOnly;
  onAnimationEnd?: () => void;
  type?: AnimationType;
  scale?: number | number[];
  startScale?: number;
  delay?: number;
}

const ClaimCashCardAnimation: FC<ClaimCashCardAnimationProps> = ({
  icon,
  reward,
  finalPositon,
  type,
  onAnimationEnd,
  scale = 1,
  startScale = 1,
  delay = 0.7,
}) => {
  const [ref, measure] = useMeasure({ offsetSize: true });

  const calcAnimation = (
    finalPositon: RectReadOnly,
    startedPosition: RectReadOnly,
    type?: AnimationType,
  ): Variant => {
    const finalWidth = finalPositon.right - finalPositon.left;
    const finalHeight = finalPositon.bottom - finalPositon.top;
    const startedWidth = startedPosition.right - startedPosition.left;
    const startedHeight = startedPosition.bottom - startedPosition.top;

    const xCof = (finalWidth - startedWidth) / 2;
    const yCof = (finalHeight - startedHeight) / 2;

    const scale_ = finalWidth / startedWidth;
    const x = finalPositon.x - measure.x + xCof;
    const y = finalPositon.y - measure.y + yCof;

    const cardsCoords = generateDetailedArc({
      x0: 0,
      y0: 0,
      x1: x,
      y1: y,
    });

    return {
      x: type === 'arc' ? [null, ...cardsCoords.leftFrames, x] : [null, x, x],
      y: type === 'arc' ? [null, ...cardsCoords.topFrames, y] : [null, y, y],
      scale:
        typeof scale === 'number' ? [startScale, 1, scale_ * scale] : scale,
      opacity: [0, 1, 1, 0],
    };
  };

  const variants: Variants | undefined = finalPositon && {
    claim: (index) => ({
      ...calcAnimation(finalPositon, measure, type),
      transition: {
        duration: 0.5,
        delay: index * 0.1 + delay,
        times: [0, 0.6, 1],
        ease: 'easeOut',
        opacity: {
          times: [0, 0.02, 0.98, 1],
          duration: 0.5,
          delay: index * 0.1 + delay,
        },
      },
    }),
  };

  return (
    <ClaimItemAnimation
      ref={ref}
      className="absolute top-0 left-0 w-full h-full z-[999]"
      quantityOfItems={6}
      itemProps={{
        ...icon,
        alt: reward,
        initial: { x: 0, y: 0, scale: 1 },
        variants,
        animate: 'claim',
      }}
      completeCallback={(index) => {
        if (index === 5) onAnimationEnd?.();
      }}
    />
  );
};

export { ClaimCashCardAnimation };
